import { dashPattern, dashRuns, runContains, joinArc } from "./dashes";
import { roundedPolygonOutline } from "./polygon-corners";
import { cornerRadius, roundedBoxOutline, roundedBoxDistance } from "./corners";
import type { Point, SceneNode } from "../scene/model";
import { polygonOutline, polygonParameters } from "./shapes";
import { shapeStyle, strokeRadius } from "./appearance";

/** Centered shape coordinates in document pixels, before rotation/shear. */
export function localOutline(
  node: SceneNode,
  ellipseSegments = 128,
  polygonTolerance = 0.01,
): Point[] {
  const w = node.geometry.width!,
    h = node.geometry.height!;
  if (node.kind === "polygon" && cornerRadius(node) > 0)
    return roundedPolygonOutline(node, polygonTolerance);
  if (cornerRadius(node) > 0) return roundedBoxOutline(node, ellipseSegments);
  if (node.kind === "ellipse")
    return Array.from({ length: ellipseSegments }, (_, i) => ({
      x: (Math.cos((i * Math.PI * 2) / ellipseSegments) * w) / 2,
      y: (Math.sin((i * Math.PI * 2) / ellipseSegments) * h) / 2,
    }));
  const points =
    node.kind === "polygon"
      ? polygonOutline(polygonParameters(node)).points
      : [
          { x: -0.5, y: -0.5 },
          { x: 0.5, y: -0.5 },
          { x: 0.5, y: 0.5 },
          { x: -0.5, y: 0.5 },
        ];
  return points.map((p) => ({ x: p.x * w, y: p.y * h }));
}
export function segmentDistance(p: Point, a: Point, b: Point) {
  const dx = b.x - a.x,
    dy = b.y - a.y,
    length2 = dx * dx + dy * dy;
  const t = length2
    ? Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.y - a.y) * dy) / length2))
    : 0;
  return Math.hypot(p.x - a.x - t * dx, p.y - a.y - t * dy);
}
/** Closest point in the same quadrant. Distance is unimodal on this interval. */
export function ellipseDistance(p: Point, a: number, b: number) {
  const x = Math.abs(p.x),
    y = Math.abs(p.y);
  let low = 0,
    high = Math.PI / 2;
  const distance = (angle: number) =>
    (a * Math.cos(angle) - x) ** 2 + (b * Math.sin(angle) - y) ** 2;
  for (let i = 0; i < 64; i++) {
    const l = low + (high - low) / 3,
      r = high - (high - low) / 3;
    if (distance(l) < distance(r)) high = r;
    else low = l;
  }
  return Math.sqrt(
    Math.min(distance(0), distance(Math.PI / 2), distance((low + high) / 2)),
  );
}
export function distanceToOutline(node: SceneNode, p: Point) {
  if (node.kind !== "polygon" && cornerRadius(node) > 0)
    return Math.abs(roundedBoxDistance(node, p));
  if (node.kind === "ellipse")
    return ellipseDistance(
      p,
      node.geometry.width! / 2,
      node.geometry.height! / 2,
    );
  const points = localOutline(node);
  return Math.min(
    ...points.map((a, i) =>
      segmentDistance(p, a, points[(i + 1) % points.length]!),
    ),
  );
}
export function curveSegments(
  radiusPixels: number,
  minimum: number,
  maximum: number,
) {
  return Math.min(
    maximum,
    Math.max(
      minimum,
      Math.ceil(
        Math.PI /
          Math.acos(Math.max(-1, 1 - 0.15 / Math.max(radiusPixels, 0.15))),
      ),
    ),
  );
}
/** Stable contour sampling keeps dash phase independent of zoom and renderer. */
export function strokeRuns(node: SceneNode) {
  const segments = curveSegments(
    Math.max(node.geometry.width!, node.geometry.height!) * 15,
    64,
    8192,
  );
  return dashRuns(
    localOutline(node, segments, 0.01),
    shapeStyle(node).strokeDash ??
      dashPattern(shapeStyle(node).lineStyle, shapeStyle(node).strokeWidth),
  );
}
export function dashedStrokeContains(node: SceneNode, p: Point) {
  return strokeRuns(node).some((run) =>
    runContains(
      run,
      p,
      strokeRadius(node),
      shapeStyle(node).lineStyle === "dotted",
    ),
  );
}
/** Union of strips/joins/caps; stencil blends their coverage only once. */
export function strokeTriangles(
  node: SceneNode,
  ellipseSegments: number,
  joinSegments: number,
  polygonTolerance = 0.01,
) {
  const style = shapeStyle(node),
    radius = strokeRadius(node),
    output: number[] = [];
  const runs =
    style.lineStyle === "solid"
      ? [
          {
            points: localOutline(node, ellipseSegments, polygonTolerance),
            closed: true,
          },
        ]
      : strokeRuns(node);
  const add = (...points: Point[]) => {
    for (const p of points)
      output.push(
        p.x / node.geometry.width! + 0.5,
        p.y / node.geometry.height! + 0.5,
      );
  };
  for (const run of runs) {
    const points = run.points,
      count = points.length;
    for (let i = 0; i < (run.closed ? count : count - 1); i++) {
      const a = points[i]!,
        b = points[(i + 1) % count]!,
        dx = b.x - a.x,
        dy = b.y - a.y,
        length = Math.hypot(dx, dy);
      if (length < 1e-12) continue;
      const nx = (-dy / length) * radius,
        ny = (dx / length) * radius;
      const a1 = { x: a.x + nx, y: a.y + ny },
        a2 = { x: a.x - nx, y: a.y - ny },
        b1 = { x: b.x + nx, y: b.y + ny },
        b2 = { x: b.x - nx, y: b.y - ny };
      add(a1, a2, b1, a2, b2, b1);
    }
    points.forEach((a, i) => {
      if (
        !run.closed &&
        style.lineStyle !== "dotted" &&
        (i === 0 || i === count - 1)
      )
        return;
      const arc =
        style.lineStyle === "solid" ||
        (!run.closed && (i === 0 || i === count - 1))
          ? { start: 0, sweep: Math.PI * 2 }
          : joinArc(
              points[(i + count - 1) % count]!,
              a,
              points[(i + 1) % count]!,
            );
      const segments = Math.max(
        1,
        Math.ceil((joinSegments * Math.abs(arc.sweep)) / (Math.PI * 2)),
      );
      for (let j = 0; j < segments; j++) {
        const start = arc.start + (arc.sweep * j) / segments,
          end = arc.start + (arc.sweep * (j + 1)) / segments;
        add(
          a,
          {
            x: a.x + Math.cos(start) * radius,
            y: a.y + Math.sin(start) * radius,
          },
          { x: a.x + Math.cos(end) * radius, y: a.y + Math.sin(end) * radius },
        );
      }
    });
  }
  return new Float32Array(output);
}
